/**
 * Registros: Documentos (lista) — conteúdo de registros do tipo `documents`
 * dentro de uma base de dados. Renderizado DENTRO do shell de Databases
 * (DatabasesPage), que já provê breadcrumb + seletor de base + Configurações.
 * Aqui vive a BARRA DE AÇÕES do conteúdo (busca + view-toggle + Filtros +
 * refresh + primário) + filterbar de chips + bulk bar + duas views + mobile=cards.
 * Migrado para o Design System (padrão-lista).
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type {
  DatabaseDocumentData,
  DatabaseDocumentResponse,
  DatabaseDocumentTransferRejectionCode,
  DatabaseDocumentTransferResult,
  DocumentCategory,
  DocumentItemStatus,
} from '@troia-v3/types';
// Import de VALOR (não `import type`): é a MESMA regra que o backend usa para
// recusar o "mover" — uma regra, um lugar. `DatabaseType` é enum real (não
// union de string literal) — o modal exige o membro do enum, não a string crua.
import { DatabaseType, isDocumentIntegrationLinked } from '@troia-v3/types';
import { ImportDocumentsButton } from '../ImportDocumentsButton';
import { TransferDocumentsDialog } from '../TransferDocumentsDialog';
import { useCopyDatabaseDocuments, useDatabaseDocuments, useDeleteDatabaseDocument } from '../../hooks/useDatabaseDocuments';
import {
  FiltersButton,
  Button,
  ListPageLoading,
  ListPageEmpty,
  ListPagePagination,
  PageToolbar,
  PlainHeadCell,
  SelectAllHeadCell,
  SelectRowCell,
  SortableHeadCell,
  StatusPill,
  ToolbarSearch,
  ViewToggle,
  type StatusPillVariant,
} from '@/shared/components/ui';
import { CardActionsMenu, type CardAction } from '@/shared/components/ui/CardActionsMenu';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { useIsMobile, usePageSelection } from '@/shared/hooks';
import { Icon } from '@/shared/icons';

type DocumentDocument = DatabaseDocumentResponse<DatabaseDocumentData>;

const categoryLabels: Record<DocumentCategory, string> = {
  article: 'Artigo',
  manual: 'Manual',
  faq: 'FAQ',
  policy: 'Política',
  guide: 'Guia',
  tutorial: 'Tutorial',
  report: 'Relatório',
  whitepaper: 'Whitepaper',
  case_study: 'Caso de Estudo',
  other: 'Outros',
};

const statusLabels: Record<DocumentItemStatus, string> = {
  published: 'Publicado',
  draft: 'Rascunho',
  archived: 'Arquivado',
};

/** Semântica de cor do status → StatusPill shared (F62; "Rascunho" = neutral). */
const statusVariants: Record<DocumentItemStatus, StatusPillVariant> = {
  published: 'success',
  draft: 'neutral',
  archived: 'neutral',
};

const categoryLabel = (cat?: string): string =>
  (cat && categoryLabels[cat as DocumentCategory]) || cat || 'N/A';

const formatDate = (value?: string): string =>
  value ? new Date(value).toLocaleDateString('pt-BR') : '—';

/** Motivo da recusa item a item, em português (o servidor manda só o código). */
const rejectionLabels: Record<DatabaseDocumentTransferRejectionCode, string> = {
  not_found: 'registro não encontrado',
  wrong_type: 'a base de destino é de outro tipo',
  inactive_target: 'a base de destino está inativa',
  integration_target: 'a base de destino é de integração',
  integration_linked: 'o registro veio de integração (ERP)',
  same_database: 'o destino é a própria base atual',
  write_failed: 'falha ao gravar',
};

const rejectionLabel = (code?: DatabaseDocumentTransferRejectionCode): string =>
  (code && rejectionLabels[code]) || 'motivo não informado';

/** Motivo do "Mover" bloqueado (D1) — vira dica no computador. */
const MOVE_BLOCKED_REASON = 'Registro veio de integração (ERP) e não pode ser movido';

export interface DocumentActionHandlers {
  onOpen: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onCopy: (id: string) => void;
  onMove: (id: string) => void;
}

/**
 * Ações do menu ••• (compartilhadas entre card e linha).
 * No celular o menu vira folha de ações, que ignora `disabledReason` — por isso,
 * ali, o motivo do "Mover" bloqueado entra no próprio rótulo.
 */
const buildDocumentActions = (
  doc: DocumentDocument,
  handlers: DocumentActionHandlers,
  isMobile: boolean,
): CardAction[] => {
  const id = doc.id;
  const moveBlocked = isDocumentIntegrationLinked(doc);
  return [
    { label: 'Abrir', icon: 'eye', onClick: () => handlers.onOpen(id) },
    { label: 'Editar', icon: 'edit', onClick: () => handlers.onEdit(id) },
    { label: 'Duplicar aqui', icon: 'copy', onClick: () => handlers.onDuplicate(id), dividerBefore: true },
    { label: 'Copiar para outra base', icon: 'copy', onClick: () => handlers.onCopy(id) },
    {
      label: moveBlocked && isMobile ? 'Mover para outra base (registro do ERP)' : 'Mover para outra base',
      icon: 'arrow-right',
      onClick: () => handlers.onMove(id),
      disabled: moveBlocked,
      disabledReason: moveBlocked ? MOVE_BLOCKED_REASON : undefined,
    },
    { label: 'Excluir', icon: 'trash', onClick: () => handlers.onDelete(id), variant: 'danger', dividerBefore: true },
  ];
};

type SortKey = 'title' | 'category' | 'status' | 'updated';

interface DocumentsContentProps {
  databaseId: string;
}

export const DocumentsContent: React.FC<DocumentsContentProps> = ({ databaseId }) => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState<DocumentCategory | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<DocumentItemStatus | undefined>(undefined);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'updated', dir: 'desc' });
  /** Alvo do modal de copiar/mover; `null` = fechado. */
  const [transfer, setTransfer] = useState<{ mode: 'copy' | 'move'; documentIds: string[] } | null>(null);

  const { data, isLoading, isFetching, error, refetch } = useDatabaseDocuments<DatabaseDocumentData>({
    databaseId,
    filters: {
      type: 'documents',
      search: searchTerm || undefined,
      ...(statusFilter ? { itemStatus: statusFilter } : {}),
      // `data.`-prefixed keys passam direto pelo repositório (categoria não é chave reservada).
      ...(categoryFilter ? { 'data.category': categoryFilter } : {}),
    },
    page: currentPage,
    limit: 12,
  });

  const items = useMemo(() => data?.items ?? [], [data]);

  // Sort client-side na página carregada (paridade com o padrão-lista de Usuários).
  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'title') cmp = (a.data?.title ?? '').localeCompare(b.data?.title ?? '', 'pt-BR');
      else if (sort.key === 'category') cmp = categoryLabel(a.data?.category).localeCompare(categoryLabel(b.data?.category), 'pt-BR');
      else if (sort.key === 'status') cmp = (a.data?.itemStatus ?? '').localeCompare(b.data?.itemStatus ?? '', 'pt-BR');
      else cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sort]);

  const deleteDocument = useDeleteDatabaseDocument();
  const copyDocuments = useCopyDatabaseDocuments();

  // Seleção da página (hook compartilhado). A chave de reinício inclui o MODO DE
  // VISÃO: sem ele, trocar Lista↔Cards mantém a seleção viva (D4). Também limpa ao
  // paginar, buscar e filtrar — o defeito que existia aqui.
  const pageIds = useMemo(() => items.map((d) => d.id), [items]);
  const selectionResetKey = [
    databaseId,
    viewMode,
    currentPage,
    searchTerm,
    categoryFilter ?? '',
    statusFilter ?? '',
  ].join('|');
  const selection = usePageSelection({ pageIds, resetKey: selectionResetKey });

  // id → título dos 12 da página: o modal e os avisos escrevem o nome do registro
  // recusado sem nenhuma busca extra.
  const titleById = useMemo<Record<string, string>>(
    () => Object.fromEntries(items.map((d) => [d.id, d.data?.title || 'Sem título'])),
    [items],
  );

  const setSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const applyCategory = (cat?: DocumentCategory) => {
    setCategoryFilter(cat);
    setCurrentPage(1);
    setFiltersOpen(false);
  };

  const applyStatus = (status?: DocumentItemStatus) => {
    setStatusFilter(status);
    setCurrentPage(1);
    setFiltersOpen(false);
  };

  const clearAllFilters = () => {
    setCategoryFilter(undefined);
    setStatusFilter(undefined);
    setCurrentPage(1);
    setFiltersOpen(false);
  };

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const openDocument = (id: string) => navigate(`/databases/${databaseId}/documents/${id}`);
  const editDocument = (id: string) => navigate(`/databases/${databaseId}/documents/${id}/edit`);

  const handleDelete = async (id: string) => {
    if (!(await confirm({ message: 'Excluir este documento?', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    deleteDocument.mutate(id);
    selection.deselect(id);
  };

  const bulkDelete = async () => {
    if (!(await confirm({ message: `Excluir ${selection.selectedCount} documento(s) selecionado(s)?`, title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    Array.from(selection.selectedIds).forEach((id) => deleteDocument.mutate(id));
    selection.clear();
  };

  /** Um aviso para o que deu certo e outro, com nome e motivo, para o que foi recusado. */
  const reportDuplicateResult = (result: DatabaseDocumentTransferResult) => {
    if (result.summary.succeeded > 0) {
      toast.success(`${result.summary.succeeded} documento(s) duplicado(s)`);
    }
    const rejected = result.results.filter((r) => r.status === 'rejected');
    if (rejected.length > 0) {
      const details = rejected
        .map((r) => `${titleById[r.documentId] ?? r.documentId}: ${rejectionLabel(r.reasonCode)}`)
        .join('; ');
      toast.error(`${rejected.length} não ${rejected.length === 1 ? 'foi duplicado' : 'foram duplicados'} — ${details}`);
    }
  };

  /** "Duplicar aqui" = copiar com destino igual à base atual — sem modal (D2). */
  const runDuplicate = async (documentIds: string[]) => {
    if (documentIds.length === 0) return;
    try {
      const result: DatabaseDocumentTransferResult = await copyDocuments.mutateAsync({
        documentIds,
        targetDatabaseId: databaseId,
        sourceDatabaseId: databaseId,
      });
      reportDuplicateResult(result);
      selection.clear();
    } catch {
      toast.error('Não foi possível duplicar');
    }
  };

  const duplicateDocument = (id: string) => { void runDuplicate([id]); };

  const bulkDuplicate = async () => {
    const documentIds = Array.from(selection.selectedIds);
    if (documentIds.length === 0) return;
    // D9: a confirmação diz quantos registros nascem, porque cada um dispara as
    // automações de "documento criado" na base de destino.
    if (!(await confirm({
      message: `Duplicar ${documentIds.length} documento(s) nesta mesma base? Serão criados ${documentIds.length} registro(s) novo(s), e as automações de "documento criado" disparam para cada um.`,
      title: 'Duplicar aqui',
      confirmButtonText: 'Duplicar',
      variant: 'default',
    }))) return;
    await runDuplicate(documentIds);
  };

  const openTransfer = (mode: 'copy' | 'move', documentIds: string[]) => {
    if (documentIds.length === 0) return;
    setTransfer({ mode, documentIds });
  };

  const copyDocument = (id: string) => openTransfer('copy', [id]);
  const moveDocument = (id: string) => openTransfer('move', [id]);

  /** Quantos dos selecionados o backend aceitaria mover (D1). Zero = botão em massa desligado. */
  const selectedMovableCount = items.filter(
    (d) => selection.isSelected(d.id) && !isDocumentIntegrationLinked(d),
  ).length;

  const rowHandlers = {
    onOpen: openDocument,
    onEdit: editDocument,
    onDelete: handleDelete,
    onDuplicate: duplicateDocument,
    onCopy: copyDocument,
    onMove: moveDocument,
  };

  const categoryFilterLabel = categoryFilter ? categoryLabels[categoryFilter] : null;
  const statusFilterLabel = statusFilter ? statusLabels[statusFilter] : null;
  const activeFilterCount = [categoryFilterLabel, statusFilterLabel].filter(Boolean).length;

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Barra de ações do conteúdo (3 zonas — SEM breadcrumb, o shell já o tem) ── */}
      <PageToolbar
        left={<>
          {/* Esquerda: contador (reflete busca/filtros) */}
          <Icon name="file-text" className="w-4 h-4 flex-none hidden sm:inline" style={{ color: 'var(--theme-muted-text)' }} />
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
              data-testid="documents-count"
            >
              {data.total}
            </span>
          )}
        </>}
        center={
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearch}
            placeholder="Buscar documentos…"
            data-testid="documents-search"
          />
        }
        right={<>
          {/* Direita: view toggle + Filtros + refresh + Novo */}
          {/* View toggle (F09-11: shared, Lista primeiro, rótulo Cards) */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="documents-view"
          />

          {/* Filtros */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="documents-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                  data-testid="documents-filters-menu"
                >
                  {/* Status */}
                  <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Status</p>
                  {([[undefined, 'Todos'], ['published', 'Publicado'], ['draft', 'Rascunho'], ['archived', 'Arquivado']] as const).map(([val, label]) => {
                    const active = statusFilter === val;
                    return (
                      <button
                        key={label}
                        onClick={() => applyStatus(val as DocumentItemStatus | undefined)}
                        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
                        style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
                        onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                        onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        data-testid={`documents-filter-status-${val ?? 'all'}`}
                      >
                        {label}
                        {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
                      </button>
                    );
                  })}

                  {/* Categoria */}
                  <p className="px-2.5 pt-2.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Categoria</p>
                  <button
                    onClick={() => applyCategory(undefined)}
                    className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
                    style={{ color: '#374151', backgroundColor: !categoryFilter ? '#f3e8ff' : 'transparent' }}
                    onMouseEnter={(e) => { if (categoryFilter) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                    onMouseLeave={(e) => { if (categoryFilter) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    data-testid="documents-filter-category-all"
                  >
                    Todas
                    {!categoryFilter && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
                  </button>
                  {(Object.keys(categoryLabels) as DocumentCategory[]).map((cat) => {
                    const active = categoryFilter === cat;
                    return (
                      <button
                        key={cat}
                        onClick={() => applyCategory(cat)}
                        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
                        style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
                        onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                        onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        data-testid={`documents-filter-category-${cat}`}
                      >
                        <span className="truncate">{categoryLabels[cat]}</span>
                        {active && <Icon name="check" className="w-4 h-4 flex-none" style={{ color: '#8b5cf6' }} />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Refresh (F07: casca 36×36 bordada + ícone 16 + hover ghost) */}
          <button
            onClick={() => { void refetch(); }}
            className="flex items-center justify-center h-9 w-9 rounded-lg transition-colors flex-none"
            style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-muted-text)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-card-background)'; }}
            title="Atualizar dados"
            disabled={isFetching}
            data-testid="documents-refresh"
          >
            <Icon name="refresh" className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>

          {/* Importar registros (#396) — só documentos (KB) */}
          <ImportDocumentsButton databaseId={databaseId} onImported={() => void refetch()} />

          {/* Novo (único primário — Button shared, F01-03) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={() => navigate(`/databases/${databaseId}/documents/new?type=documents`)}
            data-testid="documents-new"
          >
            Novo Artigo
          </Button>
        </>}
      />

      {/* ── Filterbar (chips) ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {statusFilterLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Status: {statusFilterLabel}
              <button onClick={() => applyStatus(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de status" data-testid="documents-chip-status-remove">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          {categoryFilterLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Categoria: {categoryFilterLabel}
              <button onClick={() => applyCategory(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de categoria" data-testid="documents-chip-category-remove">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="documents-clear-filters">Limpar tudo</button>
        </div>
      )}

      {/* ── Bulk bar — D4: só na visão Lista e só no computador.
           O corte é por CSS (`hidden md:flex` = o MESMO 768px da tabela), não pelo
           `useIsMobile`, que corta em 1023px e esconderia a barra numa largura em
           que a tabela (e os quadradinhos) ainda aparecem. ── */}
      {viewMode === 'table' && selection.selectedCount > 0 && (
        <div className="flex-none hidden md:flex items-center gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selection.selectedCount} selecionado(s)</span>
          <span className="flex-1" />
          <button onClick={() => { void bulkDuplicate(); }} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-primary)' }} data-testid="documents-bulk-duplicate">
            <Icon name="copy" className="w-3.5 h-3.5" /> Duplicar aqui
          </button>
          <button onClick={() => openTransfer('copy', Array.from(selection.selectedIds))} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-primary)' }} data-testid="documents-bulk-copy">
            <Icon name="copy" className="w-3.5 h-3.5" /> Copiar para outra base
          </button>
          <button
            onClick={() => openTransfer('move', Array.from(selection.selectedIds))}
            disabled={selectedMovableCount === 0}
            className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-primary)' }}
            data-testid="documents-bulk-move"
          >
            <Icon name="arrow-right" className="w-3.5 h-3.5" />
            {selectedMovableCount === 0 ? 'Mover (registros do ERP)' : 'Mover para outra base'}
          </button>
          <button onClick={() => { void bulkDelete(); }} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="documents-bulk-delete">
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={selection.clear} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="documents-bulk-cancel">Cancelar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}>
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar documentos: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando documentos…" />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="file-text"
              message="Nenhum documento encontrado"
              description={searchTerm || activeFilterCount > 0 ? 'Tente ajustar a busca ou os filtros.' : 'Não há documentos cadastrados nesta base de dados.'}
              actionLabel="Novo Artigo"
              onAction={() => navigate(`/databases/${databaseId}/documents/new?type=documents`)}
            />
          )}

          {/* Grid (Cards) — sem checkbox (F34: seleção em massa só na view Lista) */}
          {!isLoading && !error && items.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {sortedItems.map((doc) => (
                <DocumentGridCard key={doc.id} document={doc} isMobile={isMobile} {...rowHandlers} />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && items.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {sortedItems.map((doc) => (
                  <DocumentGridCard key={doc.id} document={doc} isMobile={isMobile} {...rowHandlers} />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <SelectAllHeadCell
                        checked={selection.allOnPageSelected}
                        indeterminate={selection.someOnPageSelected && !selection.allOnPageSelected}
                        onToggle={selection.toggleAllOnPage}
                        data-testid="documents-select-all"
                      />
                      <SortableHeadCell label="Título" sortKey="title" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <SortableHeadCell label="Categoria" sortKey="category" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell label="Autor" />
                      <SortableHeadCell label="Status" sortKey="status" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <SortableHeadCell label="Atualizado" sortKey="updated" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell className="w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((doc) => (
                      <DocumentListItem
                        key={doc.id}
                        document={doc}
                        selected={selection.isSelected(doc.id)}
                        onToggleSelect={selection.toggle}
                        isMobile={isMobile}
                        {...rowHandlers}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginação — "Mostrando X–Y de N" universal (F48) */}
          {!isLoading && !error && data && (
            <ListPagePagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={setCurrentPage}
              totalItems={data.total}
              pageSize={12}
              itemLabel="documentos"
            />
          )}

          {/* Espaçador p/ o rodapé fixo mobile (#407) não cobrir o último item */}
          <div className="md:hidden h-16" />
        </div>
      </div>

      {/* Rodapé fixo mobile (#407): Importar + Cadastrar */}
      <div
        className="md:hidden fixed bottom-[72px] left-0 right-0 z-30 flex gap-2 px-3 py-2.5 border-t"
        style={{ background: 'var(--theme-overlay-card-background)', borderColor: 'var(--theme-card-border)' }}
        data-testid="documents-mobile-footer"
      >
        <ImportDocumentsButton databaseId={databaseId} onImported={() => void refetch()} fullWidth />
        <button
          onClick={() => navigate(`/databases/${databaseId}/documents/new?type=documents`)}
          className="flex-1 flex items-center justify-center gap-1.5 h-9 rounded-lg text-sm font-semibold text-white transition-colors"
          style={{ backgroundColor: 'var(--theme-button-primary)' }}
          data-testid="documents-mobile-create"
        >
          <Icon name="plus" className="w-4 h-4" /> Cadastrar
        </button>
      </div>

      {/* Copiar / Mover para outra base (D2). Montado SÓ quando aberto: os hooks de
          dentro do modal rodam antes de qualquer `if (!isOpen) return null`. */}
      {transfer && (
        <TransferDocumentsDialog
          isOpen
          mode={transfer.mode}
          sourceDatabaseId={databaseId}
          databaseType={DatabaseType.DOCUMENTS}
          documentIds={transfer.documentIds}
          titleById={titleById}
          onClose={() => setTransfer(null)}
          onTransferred={() => selection.clear()}
        />
      )}
    </div>
  );
};

interface DocumentItemProps extends DocumentActionHandlers {
  document: DocumentDocument;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  /** Celular = folha de ações (sem dica): o motivo do "Mover" bloqueado vai no rótulo. */
  isMobile: boolean;
}

/** Props do card de grid — sem seleção (F34: cards SEM checkbox). */
type DocumentGridCardProps = Omit<DocumentItemProps, 'selected' | 'onToggleSelect'>;

const StatusBadge: React.FC<{ status: DocumentItemStatus | undefined }> = ({ status }) => {
  if (!status) return null;
  return (
    <StatusPill
      variant={statusVariants[status] ?? 'neutral'}
      label={statusLabels[status] || status}
    />
  );
};

/**
 * Card de documento (view "Cards" + fallback mobile da tabela).
 * Ícone sem foto = slate (#64748b). Sem checkbox (F34) + menu •••.
 */
const DocumentGridCard: React.FC<DocumentGridCardProps> = ({ document, isMobile, onOpen, onEdit, onDelete, onDuplicate, onCopy, onMove }) => {
  const d = document.data;
  const actions = buildDocumentActions(document, { onOpen, onEdit, onDelete, onDuplicate, onCopy, onMove }, isMobile);

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors cursor-pointer"
      style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
      onClick={() => onOpen(document.id)}
      data-testid={`document-card-${document.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-[11px] flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--theme-surface-sunken)' }}>
          <Icon name="file-text" className="w-5 h-5" style={{ color: '#64748b' }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm line-clamp-2" style={{ color: 'var(--theme-card-text)' }}>
            {d?.title || 'Sem título'}
          </h3>
          {d?.summary && (
            <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--theme-muted-text)' }}>
              {d.summary}
            </p>
          )}
        </div>
        <div className="flex-shrink-0 -mr-1 -mt-1" onClick={(e) => e.stopPropagation()}>
          <CardActionsMenu actions={actions} size="sm" data-testid={`document-actions-${document.id}`} />
        </div>
      </div>

      {/* Categoria + Status */}
      <div className="mt-3 flex items-center flex-wrap gap-2">
        <span
          className="inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-full text-xs font-semibold"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
        >
          <Icon name="tag" className="w-3 h-3" />
          {categoryLabel(d?.category)}
        </span>
        <StatusBadge status={d?.itemStatus} />
      </div>

      {/* Footer: autor + data */}
      <div className="mt-3 pt-3 flex items-center justify-between gap-2" style={{ borderTop: '1px solid var(--theme-card-border)' }}>
        {d?.author ? (
          <div className="flex items-center gap-1.5 min-w-0">
            <Icon name="user" className="w-3 h-3 flex-none" style={{ color: 'var(--theme-muted-text)' }} />
            <span className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>{d.author}</span>
          </div>
        ) : <span />}
        <div className="flex items-center gap-1.5 flex-none">
          <Icon name="clock" className="w-3 h-3" style={{ color: 'var(--theme-muted-text)' }} />
          <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>{formatDate(document.updatedAt)}</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Linha de tabela (view "Lista"). Checkbox (1ª col) + título multi-linha + menu ••• (última col).
 */
const DocumentListItem: React.FC<DocumentItemProps> = ({ document, selected, onToggleSelect, isMobile, onOpen, onEdit, onDelete, onDuplicate, onCopy, onMove }) => {
  const d = document.data;
  const actions = buildDocumentActions(document, { onOpen, onEdit, onDelete, onDuplicate, onCopy, onMove }, isMobile);

  return (
    <tr
      className="border-b transition-colors cursor-pointer"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
      onClick={() => onOpen(document.id)}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      data-testid={`document-row-${document.id}`}
    >
      {/* Quadradinho — célula compartilhada: o clique morre no <td>, senão marcar abre o registro. */}
      <SelectRowCell
        checked={selected}
        onToggle={() => onToggleSelect(document.id)}
        ariaLabel={selected ? 'Desmarcar' : 'Selecionar'}
        data-testid={`document-row-check-${document.id}`}
      />

      {/* Título: ícone + título + resumo */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--theme-surface-sunken)' }}>
            <Icon name="file-text" className="w-5 h-5" style={{ color: '#64748b' }} />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm line-clamp-1" style={{ color: 'var(--theme-card-text)' }}>
              {d?.title || 'Sem título'}
            </div>
            {d?.summary && (
              <div className="text-xs mt-0.5 line-clamp-1" style={{ color: 'var(--theme-muted-text)' }}>
                {d.summary}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Categoria */}
      <td className="px-4 py-3">
        <span
          className="inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-full text-xs font-semibold"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
        >
          <Icon name="tag" className="w-3 h-3" />
          {categoryLabel(d?.category)}
        </span>
      </td>

      {/* Autor */}
      <td className="px-4 py-3">
        {d?.author ? (
          <div className="flex items-center gap-1.5">
            <Icon name="user" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
            <span className="text-sm" style={{ color: 'var(--theme-card-text)' }}>{d.author}</span>
          </div>
        ) : (
          <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>—</span>
        )}
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <StatusBadge status={d?.itemStatus} />
      </td>

      {/* Atualizado */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <Icon name="calendar" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
          <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>{formatDate(document.updatedAt)}</span>
        </div>
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14" onClick={(e) => e.stopPropagation()}>
        <CardActionsMenu actions={actions} size="sm" data-testid={`document-row-actions-${document.id}`} />
      </td>
    </tr>
  );
};
